// components/EmptyState.tsx
import { Stack, Text, Button, ThemeIcon } from '@mantine/core';
import { IconUsersGroup, IconSearchOff, IconPlus } from '@tabler/icons-react';

export interface EmptyStateProps {
  /** Switches icon/copy: 'no-data' = nothing exists yet, 'no-results' = filters/search matched nothing. */
  variant: 'no-data' | 'no-results';
  /** Noun used in generated copy, e.g. "leaders", "facilitators". */
  entityLabel: string;
  /** Shown only for variant="no-data" — e.g. "Add Leader". Omit to hide the action. */
  onAdd?: () => void;
  addLabel?: string;
  /** Shown only for variant="no-results" — clears search/filters. */
  onClearFilters?: () => void;
}

export function EmptyState({
  variant,
  entityLabel,
  onAdd,
  addLabel,
  onClearFilters,
}: EmptyStateProps) {
  const isNoData = variant === 'no-data';

  return (
    <Stack align="center" gap="xs" py="xl">
      <ThemeIcon variant="light" color="gray" size={56} radius="xl">
        {isNoData ? <IconUsersGroup size={28} /> : <IconSearchOff size={28} />}
      </ThemeIcon>

      <Text fw={600} size="md">
        {isNoData ? `No ${entityLabel} yet` : `No ${entityLabel} match your search`}
      </Text>

      <Text size="sm" c="dimmed" ta="center" maw={360}>
        {isNoData
          ? `Once ${entityLabel} are added, they'll show up here.`
          : 'Try adjusting your search term or filters.'}
      </Text>

      {isNoData && onAdd && (
        <Button mt="sm" leftSection={<IconPlus size={16} />} onClick={onAdd}>
          {addLabel ?? 'Add'}
        </Button>
      )}

      {!isNoData && onClearFilters && (
        <Button mt="sm" variant="light" onClick={onClearFilters}>
          Clear Filters
        </Button>
      )}
    </Stack>
  );
}

export default EmptyState;